import React, { useState } from 'react';
import styled from 'styled-components';
import {
  BaseProps,
  DropdownMenu,
  DropdownMenuItem,
  FlexRow,
  SvgIcon,
  useMatchMedia,
} from '@make-software/cspr-ui';

export interface NavDropdownProps extends BaseProps {
  title: React.ReactNode | string;
  itemList?: { handleClick?: () => void; component: React.ReactNode }[];
  opened?: boolean;
  stickToRight?: boolean;
  hideArrowButton?: boolean;
  spotlightTargetClassName?: string;
}

const RelativeContainer = styled.ul(({ theme }) => ({
  position: 'relative',
  padding: 0,
  margin: 0,
}));

const NavDropdownContainer = styled.div<{
  stickTo?: 'left' | 'right';
}>(({ theme, stickTo = 'left' }) => ({
  paddingTop: '20px',
  position: 'absolute',
  left: 'auto',
  right: stickTo === 'left' ? 'auto' : 0,
  top: 6,
  zIndex: theme.zIndex.dropdown,
  //added to fix safari bug with z-index
  transform: 'translate3d(0, 0, 1rem)',
}));

const ArrowSvgIcon = styled(SvgIcon)<{ isOpen: boolean }>(
  ({ theme, isOpen }) => ({
    path: {
      fill: isOpen
        ? theme.styleguideColors.contentOnFill
        : theme.styleguideColors.contentTertiary,
    },
  })
);

export const NavDropdownInner = React.forwardRef<
  HTMLDivElement,
  NavDropdownProps & { isOpen: boolean }
>(
  (
    {
      title,
      itemList,
      stickToRight,
      hideArrowButton = false,
      isOpen,
      children,
    }: NavDropdownProps & { isOpen: boolean },
    ref
  ) => (
    <>
      <FlexRow ref={ref} align="center">
        {title}
        {!hideArrowButton && (
          <ArrowSvgIcon
            src="assets/icons/ic-arrow-down.svg"
            style={{ marginLeft: 8 }}
            isOpen={isOpen}
            rotate={isOpen}
          />
        )}
      </FlexRow>

      {isOpen && (
        <NavDropdownContainer stickTo={stickToRight ? 'right' : 'left'}>
          <DropdownMenu opened={isOpen}>
            {itemList &&
              itemList
                .filter(({ component }) => component != null)
                .map(({ handleClick, component }, index) => (
                  <DropdownMenuItem
                    key={index}
                    padding={'12px 16px'}
                    onClick={handleClick && handleClick}
                  >
                    {component}
                  </DropdownMenuItem>
                ))}
          </DropdownMenu>
        </NavDropdownContainer>
      )}
    </>
  )
);

export function NavDropdownV2(props: NavDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const screenType = useMatchMedia(['mobile', 'mobile', 'desktop'], []);

  const handleMouseOver = () => {
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  return screenType === 'mobile' ? (
    <RelativeContainer onClick={handleClick}>
      <NavDropdownInner isOpen={isOpen} {...props} />
    </RelativeContainer>
  ) : (
    <RelativeContainer
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      className={props.spotlightTargetClassName}
    >
      <NavDropdownInner isOpen={isOpen} {...props} />
    </RelativeContainer>
  );
}

export default NavDropdownV2;
